/**
 * How much of the server rail fits in the horizontal header.
 *
 * Pure so the arithmetic can be tested: getting it wrong is not a cosmetic
 * matter. The header is a flex row whose avatar container may shrink below its
 * content (`min-w-0`) while the avatars themselves don't shrink — so anything
 * that doesn't fit is not clipped or scrolled, it is PAINTED OVER the buttons
 * on the right. That is what a user photographed on mobile web: the "+N"
 * overflow chip sitting on top of the «BETA» button.
 *
 * Two things the previous version got wrong:
 *   1. It reserved a single hard-coded width for everything that isn't an
 *      avatar. The right-hand cluster varies (moderator-only Beta button, the
 *      download link only on web) and on a phone the guess was ~70 px short.
 *   2. It counted the "+N" stack as ONE avatar slot, when the stack is up to
 *      four overlapping avatars (~150 px).
 */

/** One avatar plus its gap. */
export const ITEM_PX = 58;
/** Home tile + add button + the gaps around them, plus a small safety margin. */
export const RESERVED_FIXED_PX = 48 + 44 + 30 + 8;
/** The "+N" tile on its own. */
export const STACK_BASE_PX = 48;
/** The same tile one size down, used when the header is too tight for the full
 *  one. A slightly smaller chip reads fine; a chip sliced in half by the clip
 *  guard does not — which is exactly what a user photographed. */
export const STACK_COMPACT_PX = 44;
/** Each avatar peeking out behind it (they overlap by 0.85rem). */
export const STACK_PREVIEW_PX = 35;
/** Most previews the stack ever shows. */
export const MAX_STACK_PREVIEWS = 3;

/** Padding the expanded-folder container adds around its tiles (px-1.5 both
 *  sides, plus a pixel of slack so a rounding error never re-introduces the
 *  clip). */
export const EXPANDED_FOLDER_PAD_PX = 13;

/** What the rail needs to know about one folder to budget space for it. */
export interface RailFolderShape {
  expanded: boolean;
  /** Servers inside it that actually resolve to a guild. */
  memberCount: number;
}

/**
 * Width the folders occupy in the horizontal rail.
 *
 * A COLLAPSED folder is one tile. An EXPANDED one is its glyph plus every
 * member tile, inside a padded container — so it can easily be four slots wide.
 *
 * Both callers used to multiply `folderCount * ITEM_PX`, i.e. they budgeted one
 * slot no matter what. Expanding a folder therefore under-measured the rail by
 * exactly the members it had just revealed: `planGuildRail` thought there was
 * more room than existed and kept too many servers visible, while
 * `railMaxWidth` capped the container below its own content and `overflow-x:
 * clip` sliced the last avatars off — with the «+» button landing on the cut.
 * Measured on a 3437px-wide window: the box was pinned to 174px for 248px of
 * content.
 */
export function folderRailWidth(folders: readonly RailFolderShape[]): number {
  let px = 0;
  for (const f of folders) {
    px += f.expanded
        ? (1 + f.memberCount) * ITEM_PX + EXPANDED_FOLDER_PAD_PX
        : ITEM_PX;
  }
  return px;
}

export interface RailPlan {
  /** Servers rendered as their own avatar, in order. */
  visibleIds: string[];
  /** Servers collapsed into the "+N" stack (empty when everything fits). */
  overflowIds: string[];
  /** How many avatars the stack may show behind its "+N" tile. */
  stackPreviews: number;
  /** Render the stack one size down — the only way it fits at all. */
  compactStack: boolean;
}

/** Width the "+N" stack occupies for a given plan. */
function stackWidth(plan: Pick<RailPlan, 'stackPreviews' | 'compactStack'>): number {
  return (
    (plan.compactStack ? STACK_COMPACT_PX : STACK_BASE_PX) +
    plan.stackPreviews * STACK_PREVIEW_PX
  );
}

export function planGuildRail(input: {
  /** Ungrouped servers, in rail order. */
  ungroupedOrder: string[];
  /** Measured header width. */
  headerWidth: number;
  /** Measured width of the right-hand action cluster. */
  actionsWidth: number;
  /** Space the folders occupy — see folderRailWidth. Not a count: an expanded
   *  folder is several slots wide. */
  foldersPx: number;
  /** Kept visible even if it would otherwise overflow. */
  currentGuildId: string | null;
}): RailPlan {
  const { ungroupedOrder, headerWidth, actionsWidth, foldersPx, currentGuildId } = input;

  const room = headerWidth - RESERVED_FIXED_PX - actionsWidth - foldersPx;

  if (ungroupedOrder.length * ITEM_PX <= room) {
    return {
      visibleIds: ungroupedOrder,
      overflowIds: [],
      stackPreviews: MAX_STACK_PREVIEWS,
      compactStack: false,
    };
  }

  // Shrink the stack (fewer peeking avatars) until it fits, then spend what's
  // left on real avatars. If even the bare tile doesn't fit, go one size down
  // rather than let the clip guard bisect it.
  let stackPreviews = MAX_STACK_PREVIEWS;
  while (stackPreviews > 0 && STACK_BASE_PX + stackPreviews * STACK_PREVIEW_PX > room) {
    stackPreviews -= 1;
  }
  const compactStack = stackPreviews === 0 && STACK_BASE_PX > room;
  const stackPx = stackWidth({ stackPreviews, compactStack });
  const slots = Math.max(0, Math.floor((room - stackPx) / ITEM_PX));

  let visibleIds = ungroupedOrder.slice(0, slots);
  if (
    slots > 0 &&
    currentGuildId &&
    !visibleIds.includes(currentGuildId) &&
    ungroupedOrder.includes(currentGuildId)
  ) {
    visibleIds = [...visibleIds.slice(0, slots - 1), currentGuildId];
  }
  const visible = new Set(visibleIds);
  return {
    visibleIds,
    overflowIds: ungroupedOrder.filter((id) => !visible.has(id)),
    stackPreviews,
    compactStack,
  };
}

/**
 * Width the rail container should be capped at.
 *
 * Normally the plan already fits, and the cap is simply what the plan needs —
 * so the `overflow-x: clip` guard never actually cuts anything. It only bites
 * in the degenerate case (folders always render and can alone exceed the
 * header), where clipping beats painting over the buttons.
 *
 * Capping at the raw available room instead was a bug of its own: the box
 * ended mid-avatar, and the user saw a server sliced in half.
 */
export function railMaxWidth(plan: RailPlan, foldersPx: number, room: number): number {
  return Math.max(0, Math.min(railPlanWidth(plan, foldersPx), Math.max(room, 0)));
}

/** Total width the plan asks for — the header must not be narrower than this. */
export function railPlanWidth(plan: RailPlan, foldersPx: number): number {
  const stack = plan.overflowIds.length > 0 ? stackWidth(plan) : 0;
  return plan.visibleIds.length * ITEM_PX + foldersPx + stack;
}
